import { useCallback, useEffect, useRef, useState } from "react";
import { t } from "@/i18n-js/instance";
import { IconButton } from "@/react/components/shared/uikit/HeaderV3/IconButton";
import { LoaderRow } from "../../shared/LoaderRow";
import { Typography } from "../../shared/uikit/Typography";

interface HorizontalListInfiniteScrollProps<T> {
  containerClassName?: string;
  fetchNextPage: () => Promise<unknown>;
  hasMore: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  items: T[];
  listClassName?: string;
  renderItem: (item: T) => React.ReactNode;
  scrollOffset?: number;
  title?: string;
  totalCount: number;
}

export const HorizontalListInfiniteScroll = <T,>({
  title,
  items,
  renderItem,
  hasMore,
  fetchNextPage,
  isFetchingNextPage,
  isLoading,
  scrollOffset = 500,
  containerClassName,
  listClassName,
  totalCount,
}: HorizontalListInfiniteScrollProps<T>) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [shouldShowLeftFade, setShouldShowLeftFade] = useState(false);
  const [shouldShowRightFade, setShouldShowRightFade] = useState(false);

  const scrollByOffset = (offset: number) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: offset, behavior: "smooth" });
    }
  };

  const updateFadeVisibility = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const canScrollLeft = el.scrollWidth > el.clientWidth && el.scrollLeft > 0;
    const canScrollRight =
      (el.scrollWidth > el.clientWidth &&
        el.scrollLeft + el.clientWidth < el.scrollWidth - 1) ||
      hasMore;
    setShouldShowLeftFade(canScrollLeft);
    setShouldShowRightFade(canScrollRight);
  }, [hasMore]);

  useEffect(() => {
    if (!scrollRef.current || !sentinelRef.current) return;
    const rootEl = scrollRef.current;
    const sentinel = sentinelRef.current;

    const observer = new IntersectionObserver(
      entries => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMore && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { root: rootEl, threshold: 1.0 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    updateFadeVisibility();
  }, [items.length, hasMore, isFetchingNextPage, updateFadeVisibility]);

  return (
    <div className={containerClassName}>
      <div className="mb-2 flex justify-between gap-2">
        <div className="flex items-center gap-x-2">
          <Typography.LabelLg weight="semibold">{title}</Typography.LabelLg>
          {!!items?.length && title && (
            <span className="bg-c-sidebar-badge text-c-sidebar-badge-text inline-flex rounded-full">
              <span className="inline-block min-h-[1rem] min-w-[1rem] px-1 text-center text-[0.625rem] font-semibold leading-4">
                {totalCount > 99 ? "99+" : totalCount}
              </span>
            </span>
          )}
        </div>

        <div className="mb-2 flex gap-2">
          <IconButton
            name="20-chevron-left"
            ariaLabel={t("prev")}
            onClick={() => scrollByOffset(-scrollOffset)}
            buttonSize={28}
            iconSize={20}
            className="border-primary rounded-full border disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
            iconClassName="!h-5 !w-5 "
            disabled={!shouldShowLeftFade}
          />
          <IconButton
            name="20-chevron-right"
            ariaLabel={t("next")}
            onClick={() => scrollByOffset(scrollOffset)}
            buttonSize={28}
            iconSize={20}
            className="border-primary rounded-full border disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
            iconClassName="!h-5 !w-5"
            disabled={!shouldShowRightFade}
          />
        </div>
      </div>
      {isLoading ? (
        <LoaderRow />
      ) : (
        <div className="relative">
          <div
            ref={scrollRef}
            onScroll={updateFadeVisibility}
            className="scrollbar-hide overflow-x-auto"
          >
            <ol
              className={listClassName || "flex list-none flex-row gap-3 pr-4"}
            >
              {items.map(renderItem)}
              <div ref={sentinelRef} className="w-px shrink-0" />
            </ol>
          </div>
          {shouldShowLeftFade && (
            <div className="pointer-events-none absolute inset-y-0 left-0 w-12 bg-gradient-to-r from-[#ffffffed] to-transparent" />
          )}
          {shouldShowRightFade && (
            <div className="pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-[#ffffffed] to-transparent" />
          )}
        </div>
      )}
    </div>
  );
};
